<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  components: { type: Array, default: () => [] },
});

const emit = defineEmits(['insert']);

const search = ref('');

const LAYOUT_COMPONENTS = [
  {
    tag_name: 'portal-section',
    label: 'Section',
    is_system: true,
    is_layout: true,
  },
  {
    tag_name: 'layout-columns',
    label: 'Columns',
    is_system: true,
    is_layout: true,
  },
  {
    tag_name: 'portal-tabs',
    label: 'Tabs',
    is_system: true,
    is_layout: true,
  },
];

const FALLBACK_COMPONENTS = [
  { tag_name: 'hero', label: 'Hero', is_system: true },
  { tag_name: 'cover-photo', label: 'Cover Photo', is_system: true },
  { tag_name: 'key-info', label: 'Key Information', is_system: true },
  { tag_name: 'event-description', label: 'Description', is_system: true },
  { tag_name: 'event-schedule', label: 'Programme', is_system: true },
  { tag_name: 'event-floorplans', label: 'Floorplans', is_system: true },
  { tag_name: 'pricing-table', label: 'Pricing', is_system: true },
  { tag_name: 'event-quote', label: 'Quote', is_system: true },
  { tag_name: 'accept-quote', label: 'Accept Quote', is_system: true },
  { tag_name: 'attributes-panel', label: 'Attributes', is_system: true },
  { tag_name: 'attributes-form', label: 'Attributes Form', is_system: true },
  { tag_name: 'event-manager', label: 'Event Manager', is_system: true },
  { tag_name: 'portal-tabs', label: 'Tabs', is_system: true },
  { tag_name: 'portal-tab', label: 'Tab Panel', is_system: true },
  { tag_name: 'event-documents', label: 'Documents', is_system: true },
  { tag_name: 'section-divider', label: 'Divider', is_system: true },
  { tag_name: 'content-heading', label: 'Heading', is_system: true },
  { tag_name: 'content-text', label: 'Text', is_system: true },
  { tag_name: 'content-image', label: 'Image', is_system: true },
  { tag_name: 'content-button', label: 'Button', is_system: true },
];

const ICON_MAP = {
  'cover-photo': 'i-lucide-image',
  hero: 'i-lucide-image',
  'key-info': 'i-lucide-info',
  'event-description': 'i-lucide-align-left',
  'event-schedule': 'i-lucide-calendar',
  'event-floorplans': 'i-lucide-map',
  'pricing-table': 'i-lucide-receipt',
  'event-quote': 'i-lucide-scroll-text',
  'accept-quote': 'i-lucide-check-square',
  'attributes-panel': 'i-lucide-list',
  'attributes-form': 'i-lucide-pen-line',
  'section-divider': 'i-lucide-minus',
  'content-heading': 'i-lucide-heading',
  'content-text': 'i-lucide-type',
  'content-image': 'i-lucide-image-plus',
  'content-button': 'i-lucide-mouse-pointer-click',
  'event-manager': 'i-lucide-handshake',
  'portal-tabs': 'i-lucide-layout-panel-top',
  'portal-tab': 'i-lucide-panel-top',
  'event-documents': 'i-lucide-file-text',
  'portal-section': 'i-lucide-square',
  'layout-columns': 'i-lucide-columns-2',
};

const DESCRIPTION_MAP = {
  hero: 'Cover with date and title — overlay or stacked card',
  'cover-photo': 'Standalone cover image block',
  'key-info': 'Location, date, guests and logistics summary',
  'event-description': 'Event description from planning data',
  'event-schedule': 'Programme timeline from allocations',
  'event-floorplans': 'Space floorplan previews',
  'pricing-table': 'Live pricing sheet totals and line items',
  'event-quote': 'Issued quote line items with link to sign',
  'accept-quote': 'Button linking to the quote signing page',
  'attributes-panel': 'Catering, AV and custom attributes',
  'attributes-form': 'Customer-editable event attributes',
  'event-manager': 'Assigned manager contact card',
  'portal-tabs': 'In-page tabbed panels for grouping content',
  'portal-tab': 'Single tab panel inside Tabs',
  'event-documents': 'Shared files from the conversation',
  'section-divider': 'Visual separator between sections',
  'content-heading': 'Editable heading text',
  'content-text': 'Editable paragraph text',
  'content-image': 'Upload or pick a custom image',
  'content-button': 'Link button with label',
  'portal-section': 'Full-width section container for grouping',
  'layout-columns': 'Multi-column layout — set column count on the canvas',
};

const CONTENT_TAGS = [
  'content-heading',
  'content-text',
  'content-image',
  'content-button',
];

const HIDDEN_FROM_DATA = ['portal-section', 'portal-tabs', 'portal-tab'];

const LAYOUT_TAGS = LAYOUT_COMPONENTS.map(c => c.tag_name);

const getIcon = tagName => ICON_MAP[tagName] || 'i-lucide-box';
const getDescription = tagName =>
  DESCRIPTION_MAP[tagName] || 'Custom portal element';

const availableComponents = computed(() =>
  props.components.length ? props.components : FALLBACK_COMPONENTS
);

const matchesSearch = c => {
  const q = search.value.trim().toLowerCase();
  if (!q) return true;
  const description = getDescription(c.tag_name).toLowerCase();
  return (
    (c.label || '').toLowerCase().includes(q) ||
    (c.tag_name || '').toLowerCase().includes(q) ||
    description.includes(q)
  );
};

const sections = computed(() => {
  const layout = LAYOUT_COMPONENTS.filter(matchesSearch);
  const content = availableComponents.value.filter(
    c => c.is_system && CONTENT_TAGS.includes(c.tag_name) && matchesSearch(c)
  );
  const data = availableComponents.value.filter(
    c =>
      c.is_system &&
      !CONTENT_TAGS.includes(c.tag_name) &&
      !LAYOUT_TAGS.includes(c.tag_name) &&
      !HIDDEN_FROM_DATA.includes(c.tag_name) &&
      matchesSearch(c)
  );
  const custom = availableComponents.value.filter(
    c => !c.is_system && matchesSearch(c)
  );

  return [
    { key: 'layout', title: 'Layout', items: layout },
    { key: 'content', title: 'Content', items: content },
    { key: 'data', title: 'Event data', items: data },
    { key: 'custom', title: 'Custom', items: custom },
  ].filter(section => section.items.length);
});

const hasAny = computed(() => sections.value.length > 0);

const onDragStart = (e, tagName) => {
  e.dataTransfer.setData('component/tag', tagName);
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="px-3 pt-3 pb-2">
      <div class="relative">
        <span
          class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-8 pointer-events-none"
        />
        <input
          v-model="search"
          type="search"
          placeholder="Search elements..."
          class="reset-base w-full pl-8 pr-3 py-2 text-xs border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 placeholder:text-n-slate-8 outline-none focus:border-n-brand-7 focus:ring-1 focus:ring-n-brand-4"
        />
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-3 pb-4 space-y-4">
      <div v-if="!hasAny" class="px-1 py-8 text-center">
        <p class="text-xs text-n-slate-9">No elements found</p>
      </div>

      <div v-for="section in sections" :key="section.key">
        <p
          class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider mb-2 px-0.5"
        >
          {{ section.title }}
        </p>
        <div class="space-y-1.5">
          <button
            v-for="comp in section.items"
            :key="comp.tag_name"
            type="button"
            class="group flex items-start gap-3 w-full p-2.5 rounded-xl border border-n-slate-5 bg-white hover:border-n-brand-7 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing text-left"
            draggable="true"
            @dragstart="onDragStart($event, comp.tag_name)"
            @click="emit('insert', comp.tag_name)"
          >
            <span
              class="flex items-center justify-center size-9 shrink-0 rounded-lg bg-n-slate-2 text-n-slate-11 group-hover:bg-n-brand-2 group-hover:text-n-brand-11 transition-colors"
            >
              <span
                :class="
                  section.key === 'custom'
                    ? 'i-lucide-puzzle'
                    : getIcon(comp.tag_name)
                "
                class="size-4"
              />
            </span>
            <span class="min-w-0 flex-1 pt-0.5">
              <span
                class="block text-xs font-semibold text-n-slate-12 leading-tight"
              >
                {{ comp.label || comp.tag_name }}
              </span>
              <span
                class="block text-[11px] text-n-slate-9 leading-snug mt-0.5"
              >
                {{ getDescription(comp.tag_name) }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
